import styled from "styled-components";
import { useContext, useEffect, useState } from "react";
import {
  saveOrUpdateReport,
  writeToHotTopicsDatabase,
  deleteReport,
} from "../../../Firebase";
import CustomSlider from "../../UI/Slider/Slider";
import PercentSlider from "../../UI/Slider/PercentSlider";
import NumberInput from "../../UI/NumberInput";

import { Tooltip } from "primereact/tooltip";

import { Button } from "primereact/button";
import Lottie from "react-lottie-player";

import { ReportDatabaseContext } from "../../../Contexts/ReportDatabaseContext";
import { CurrentUserContext } from "../../../Contexts/CurrentUserContext";
import { OrgTreeContext } from "../../../Contexts/OrgTreeContext";
import { HotTopicsContext } from "../../../Contexts/HotTopicsContext";

import { formatDate } from "../../../Helpers/TimeFunctions";
import confettiLottie from "../../UI/Animations/confetti-lottie.json";
import "react-widgets/styles.css";
import TimeShareVisual from "../TimeShareVisusal";
import HotTopicsMenu from "../HotTopicsMenu";
import { useNavigate, useLocation } from "react-router-dom";
import {
  emptyReport,
  emptyReportNoOptional,
  generateRandomString,
  combineReports,
} from "../../../Helpers/ReportDataFunctions";
import { findNodeById, getTeamLabel } from "../../../Helpers/OrgTreeFunctions";

const ReportPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  margin: 0 auto;
  -webkit-user-select: none;
  user-select: none;
  height: calc(100vh - 85px - 85px);
  overflow: scroll;

  @media (min-width: 800px) {
    height: calc(100vh - 85px);
  }

  .save-button {
    justify-self: center;
    padding: 20px 30px;
    margin: 10px;
  }
  .confetti-lottie {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  p.intro {
    position: relative;
    font-size: 16px;
    text-align: center;
    font-weight: 400;
    margin-top: 20px;
    color: var(--accent-color-dark);
    max-width: 700px;
    box-shadow: 0px 0px 6px 4px rgba(197, 197, 197, 0.715);
    border-radius: 7px;
    padding: 25px 15px 15px 15px;
  }

  p.intro.hidden {
    box-shadow: none;
    margin-top: 0;
    max-width: 80%;
    padding: 10px 0 0 0;
  }
  .pi-times {
    position: absolute;
    top: 7px;
    right: 5px;
    cursor: pointer;
    font-size: 14px;
  }

  @media (max-width: 800px) {
    p.intro.hidden {
      font-size: 14px;
      max-width: 100%;
      padding: 5px 0 0 0;
    }
  }
`;

const SpacerLine = styled.div`
  width: 100%;
  border-bottom: 2px solid
    ${(props) => props.color || "var(--accent-color-dark)"};
  margin: 10px 0;
`;
const DateAndSaveBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  max-width: 700px;
  align-items: center;
  justify-content: space-between;
  z-index: 4;
  background-color: var(--accent-color-dark);
  padding: 7px;
  border-radius: 7px;
  box-shadow: 0px 0px 6px 4px rgba(197, 197, 197, 0.715);
`;

const ReportInfoBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 3px;
  color: white;
  font-size: 14px;
  max-width: 700px;
`;

const SectionWrapper = styled.div`
  max-width: 700px;
  padding-top: 20px;
  width: 100%;
  h2 {
    color: var(--accent-color-dark);
    font-weight: 600;
    font-size: 18px;
    margin-bottom: 10px;
  }
  p {
    font-size: 12px;
  }
`;

const QuestionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 700px;
  width: 100%;

  &.lifeSkills .MuiSlider-root {
    color: var(--lifeSkills-color);
  }
  &.relationships .MuiSlider-root {
    color: var(--relationships-color);
  }
  &.health .MuiSlider-root {
    color: var(--health-color);
  }
  &.additionalNeeds .MuiSlider-root {
    color: var(--additionalNeeds-color);
  }
  &.mentalHealth .MuiSlider-root {
    color: var(--mentalHealth-color);
  }
  &.trauma .MuiSlider-root {
    color: var(--trauma-color);
  }
  &.crisis .MuiSlider-root {
    color: var(--crisis-color);
  }
  &.admin .MuiSlider-root {
    color: var(--admin-color);
  }
  &.personalDevelopment .MuiSlider-root {
    color: var(--personalDevelopment-color);
  }

  h3 {
    font-size: 15px;
    font-weight: 400;
    display: inline-block;
    max-width: 200px;
  }

  h4 {
    font-size: 15px;
    font-weight: 300;
    padding: 10px 0;
    text-align: center;
    border-radius: 4px;
    border: 1px solid #cecece;
    min-width: 120px;
  }
`;

export default function EditReportPage() {
  const userContext = useContext(CurrentUserContext);
  const user = userContext?.currentUser;
  const reportContext = useContext(ReportDatabaseContext);
  const reportDatabase = reportContext.reportData;
  const orgTree = useContext(OrgTreeContext);
  const hotTopicsContext = useContext(HotTopicsContext);

  const [showIntroText, setShowIntroText] = useState(true);
  const [selectedPeriode, setSelectedPeriode] = useState<Date[]>();
  const [confetti, setConfetti] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<string>();
  const [selectedReportData, setSelectedReportData] =
    useState<ReportData | null>(null);
  const [timeInPerson, setTimeInPerson] = useState(0);
  const [timeShare, setTimeShare] = useState({
    crisis: 0,
    relationships: 0,
    lifeSkills: 0,
    health: 0,
    additionalNeeds: 0,
    mentalHealth: 0,
    trauma: 0,
    admin: 0,
    personalDevelopment: 0,
  });
  const [totalPastoralHours, setTotalPastoralHours] = useState(0);
  const [teamLeaderReport, setTeamLeaderReport] = useState({
    prepareAndEnrich: 0,
    weddings: 0,
    childDeath: 0,
    sucideDeath: 0,
    stillBorn: 0,
    funerals: 0,
    dfv: 0,
    hospitalVisits: 0,
    sucidalIdeation: 0,
    flowers: 0,
    meals: 0,
    kiloOfKindness: 0,
  });
  const navigate = useNavigate();

  const confettiPop = () => {
    setConfetti(true);
    setTimeout(() => {
      setConfetti(false);
    }, 2000);
  };
  const pathSegments = useLocation().pathname.split("/");
  const dateAndTeamSegment = pathSegments[3];
  // Split the segment to get the start date, end date, and team ID
  const [startDateFromParam, endDateAndTeam] = dateAndTeamSegment.split("to");
  const [endDateFromParam, selectedTeamFromParam] = endDateAndTeam.split("in");
  const [hotTopics, setHotTopics] = useState<string[]>([]);

  // get the periode from the url and set it as selectedPeriode
  useEffect(() => {
    startDateFromParam &&
      endDateFromParam &&
      setSelectedPeriode([
        new Date(startDateFromParam),
        new Date(endDateFromParam),
      ]);
    selectedTeamFromParam && setSelectedTeam(selectedTeamFromParam);
  }, []);

  //get report from the time periode and set to selectedReportData
  useEffect(() => {
    if (reportDatabase && user && selectedPeriode && selectedTeam) {
      const matchingReports = reportDatabase.filter(
        (report) =>
          new Date(report.reportPeriodDate) >= new Date(selectedPeriode[0]) &&
          new Date(report.reportPeriodDate) <= new Date(selectedPeriode[1]) &&
          report.userId === user.userId &&
          report.teamId === selectedTeam
      );
      if (matchingReports) {
        //combine all reports values into one
        const commbinedReport: ReportData = combineReports(matchingReports);
        setSelectedReportData(commbinedReport);
      } else {
        const reportCopy = { ...emptyReport };
        setSelectedReportData(reportCopy);
      }
    }
  }, [reportDatabase, user, selectedPeriode, selectedTeam]);
  // set the timeShare and teamLeaderReportstate with the selectedReportData
  useEffect(() => {
    if (selectedReportData) {
      setTimeShare({
        crisis: selectedReportData.crisisHours ?? 0,
        relationships: selectedReportData.relationshipHours ?? 0,
        lifeSkills: selectedReportData.lifeSkillsHours ?? 0,
        health: selectedReportData.healthHours ?? 0,
        additionalNeeds: selectedReportData.addNeedsHours ?? 0,
        mentalHealth: selectedReportData.mentalHealthHours ?? 0,
        trauma: selectedReportData.traumaHours ?? 0,
        admin: selectedReportData.adminAndMeetingsHours ?? 0,
        personalDevelopment:
          selectedReportData.professionalDevelopmentHours ?? 0,
      });

      setTeamLeaderReport({
        prepareAndEnrich: selectedReportData.prepAndEnrichAppointments ?? 0,
        weddings: selectedReportData.weedingsConducted ?? 0,
        childDeath: selectedReportData.childDeath ?? 0,
        sucideDeath: selectedReportData.sucideDeath ?? 0,
        stillBorn: selectedReportData.stillBorn ?? 0,
        funerals: selectedReportData.funeralsConducted ?? 0,
        dfv: selectedReportData.newDFVIncidents ?? 0,
        hospitalVisits: selectedReportData.hospitalVisits ?? 0,
        sucidalIdeation: selectedReportData.suicidalIdeation ?? 0,
        flowers: selectedReportData.giftsFlowers ?? 0,
        meals: selectedReportData.meals ?? 0,
        kiloOfKindness: selectedReportData.kiloOfKindnessBags ?? 0,
      });

      selectedReportData.hotTopics &&
        setHotTopics(selectedReportData.hotTopics);

      selectedReportData.totalInPersonHours &&
        setTimeInPerson(selectedReportData.totalInPersonHours);
    }
  }, [selectedReportData]);

  useEffect(() => {
    const totalAllTimeShare =
      timeShare.crisis +
      timeShare.relationships +
      timeShare.lifeSkills +
      timeShare.health +
      timeShare.additionalNeeds +
      timeShare.mentalHealth +
      timeShare.trauma;
    setTotalPastoralHours(totalAllTimeShare);
  }, [timeShare]);

  function handleSliderChange(value: number, key: string) {
    setTimeShare((prevState) => {
      return {
        ...prevState,
        [key]: value,
      };
    });
  }

  function handleNumberPickerChange(value: number, key: string) {
    setTeamLeaderReport((prevState) => {
      return {
        ...prevState,
        [key]: value,
      };
    });
  }

  function hoursToHoursMinutes(hours: number) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}h${minutes}m`;
  }

  function handleSave() {
    if (
      !selectedReportData ||
      !selectedPeriode ||
      !user ||
      !orgTree ||
      !selectedTeam
    ) {
      let missingData = "";
      if (!selectedReportData) missingData = "Selected ReportData";
      if (!selectedPeriode) missingData = "Selected Periode";
      if (!user) missingData = "User Data";
      if (!orgTree) missingData = "Org Tree";
      window.alert(`Something went wrong, missing: ${missingData}`);
      return;
    }
    const userRole = user.PCRoles.find(
      //only need to put the role if the person is reporting hours
      //if the report is oversight only their involvement doesn't matter because
      //they are only reporting data for the whole team
      (roleObj) =>
        roleObj.teamId === selectedTeam &&
        (roleObj.role === "pastoralCare" ||
          roleObj.role === "volunteer" ||
          roleObj.role === "churchStaff")
    );

    const team = selectedTeam && findNodeById([orgTree], selectedTeam);
    const newReport: ReportData = { ...emptyReportNoOptional };
    newReport.firstName = user.firstName;
    newReport.lastName = user.lastName;
    newReport.email = user.email[0];
    newReport.location = team ? team.label : "";
    newReport.teamId = team ? team.teamId : "";
    newReport.involvement = userRole ? userRole.role : "";
    newReport.hotTopics = hotTopics;
    //save new hot topics to hotTopicsDatabase
    hotTopics.forEach((hotTopic) => {
      const matches: string[] = [];
      hotTopicsContext.forEach((hotTopicFromContext) => {
        //check for existing hotTopic in labels
        if (
          hotTopicFromContext.label.trim().toLowerCase() ===
          hotTopic.trim().toLowerCase()
        ) {
          matches.push(hotTopicFromContext.label.trim().toLowerCase());
        }
        //check for existing hotTopic in keywords
        if (
          hotTopicFromContext.keyWords?.some(
            (hotTopicFromContext) =>
              hotTopicFromContext.trim().toLowerCase() ===
              hotTopic.trim().toLowerCase()
          )
        ) {
          matches.push(hotTopicFromContext.label.trim().toLowerCase());
        }
      });
      if (matches.length == 0) {
        writeToHotTopicsDatabase({
          label: hotTopic.trim().toLocaleLowerCase(),
          keyWords: [],
          status: "unclassified",
          occurrence: 0,
        });
      }
    });

    newReport.reportPeriodDate = selectedPeriode[1].toString();
    newReport.lastEditDate = new Date().toString();
    newReport.reportId = selectedReportData.reportId
      ? selectedReportData.reportId
      : generateRandomString();
    newReport.userId = user.userId;
    newReport.importedData = false;

    if (timeShare.admin || selectedReportData.adminAndMeetingsHours) {
      newReport.adminAndMeetingsHours = timeShare.admin;
    }

    if (
      timeShare.personalDevelopment ||
      selectedReportData.professionalDevelopmentHours
    ) {
      newReport.professionalDevelopmentHours = timeShare.personalDevelopment;
    }

    if (timeShare.crisis || selectedReportData.crisisHours) {
      newReport.crisisHours = timeShare.crisis;
    }
    if (timeShare.relationships || selectedReportData.relationshipHours) {
      newReport.relationshipHours = timeShare.relationships;
    }
    if (timeShare.lifeSkills || selectedReportData.lifeSkillsHours) {
      newReport.lifeSkillsHours = timeShare.lifeSkills;
    }
    if (timeShare.trauma || selectedReportData.traumaHours) {
      newReport.traumaHours = timeShare.trauma;
    }
    if (timeShare.additionalNeeds || selectedReportData.addNeedsHours) {
      newReport.addNeedsHours = timeShare.additionalNeeds;
    }

    if (timeShare.health || selectedReportData.healthHours) {
      newReport.healthHours = timeShare.health;
    }

    if (timeShare.mentalHealth || selectedReportData.mentalHealthHours) {
      newReport.mentalHealthHours = timeShare.mentalHealth;
    }

    if (totalPastoralHours || selectedReportData.totalPastoralHours) {
      newReport.totalPastoralHours = totalPastoralHours;
    }

    if (timeInPerson || selectedReportData.totalInPersonHours) {
      newReport.totalInPersonHours = timeInPerson;
    }

    if (
      teamLeaderReport.prepareAndEnrich ||
      selectedReportData.prepAndEnrichAppointments
    ) {
      newReport.prepAndEnrichAppointments = teamLeaderReport.prepareAndEnrich;
    }

    if (teamLeaderReport.weddings || selectedReportData.weedingsConducted) {
      newReport.weedingsConducted = teamLeaderReport.weddings;
    }

    if (teamLeaderReport.funerals || selectedReportData.funeralsConducted) {
      newReport.funeralsConducted = teamLeaderReport.funerals;
    }

    if (
      teamLeaderReport.sucidalIdeation ||
      selectedReportData.suicidalIdeation
    ) {
      newReport.suicidalIdeation = teamLeaderReport.sucidalIdeation;
    }

    if (teamLeaderReport.dfv || selectedReportData.newDFVIncidents) {
      newReport.newDFVIncidents = teamLeaderReport.dfv;
    }

    if (teamLeaderReport.hospitalVisits || selectedReportData.hospitalVisits) {
      newReport.hospitalVisits = teamLeaderReport.hospitalVisits;
    }

    if (teamLeaderReport.sucideDeath || selectedReportData.sucideDeath) {
      newReport.sucideDeath = teamLeaderReport.sucideDeath;
    }

    if (teamLeaderReport.stillBorn || selectedReportData.stillBorn) {
      newReport.stillBorn = teamLeaderReport.stillBorn;
    }

    if (teamLeaderReport.childDeath || selectedReportData.childDeath) {
      newReport.childDeath = teamLeaderReport.childDeath;
    }

    if (teamLeaderReport.flowers || selectedReportData.giftsFlowers) {
      newReport.giftsFlowers = teamLeaderReport.flowers;
    }

    if (teamLeaderReport.meals || selectedReportData.meals) {
      newReport.meals = teamLeaderReport.meals;
    }

    if (
      teamLeaderReport.kiloOfKindness ||
      selectedReportData.kiloOfKindnessBags
    ) {
      newReport.kiloOfKindnessBags = teamLeaderReport.kiloOfKindness;
    }
    //console.log(newReport);
    confettiPop();
    //delete duplicate reports
    if (!reportDatabase) return;
    const matchingReports = reportDatabase.filter(
      (report) =>
        new Date(report.reportPeriodDate) >= new Date(selectedPeriode[0]) &&
        new Date(report.reportPeriodDate) <= new Date(selectedPeriode[1]) &&
        report.userId === user.userId &&
        report.teamId === selectedTeam &&
        report.reportId !== newReport.reportId
    );
    matchingReports.forEach((report) => {
      deleteReport(report);
    });

    console.log(newReport);

    setTimeout(() => {
      saveOrUpdateReport(newReport);
      navigate("/reports");
    }, 1500);
  }

  const hideIntroText = () => {
    //set the local storage to true
    localStorage.setItem("hidePCReportingIntroText", "true");
    setShowIntroText(false);
  };

  //get from the local storage if the user has seen the intro text
  useEffect(() => {
    const hideIntroText = localStorage.getItem("hidePCReportingIntroText");
    if (hideIntroText === "true") {
      setShowIntroText(false);
    }
  }, []);

  return (
    <ReportPageWrapper>
      <DateAndSaveBox>
        <ReportInfoBox>
          <p>
            Team:{" "}
            {selectedTeam && orgTree && getTeamLabel(selectedTeam, orgTree)}
          </p>
          <p>
            Periode: {selectedPeriode && formatDate(selectedPeriode[0])} to{" "}
            {selectedPeriode && formatDate(selectedPeriode[1])}
          </p>
        </ReportInfoBox>
        <Button
          className="save-button"
          label="Save"
          severity="success"
          onClick={handleSave}
        />
      </DateAndSaveBox>
      {user &&
        user.PCRoles.some(
          (roleObj) =>
            roleObj.role === "pastoralCare" ||
            roleObj.role === "volunteer" ||
            roleObj.role === "churchStaff"
        ) && (
          <>
            {Object.values(timeShare).some((value) => value !== 0) && (
              <TimeShareVisual timeShare={timeShare} />
            )}
            {showIntroText && (
              <p className="intro">
                <i className="pi pi-times" onClick={hideIntroText}></i>
                Thank you for completing a pastoral care report. Please note
                that this report isn't intended to capture every hour of your
                work but only hours spent in key areas. <br />
                Your oversights will only be able to see a general overview of
                their team to indentify trends and provide information for our
                annual report.
                <br />
                No one exept you can have acces to your individual data.
              </p>
            )}

            <SectionWrapper>
              <h2 style={{ color: "var(--crisis-color)" }}>
                Crisis/Grief <Tooltip target=".crisisTooltip" />
                <i
                  className="crisisTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week dealing with things such as Funerals, Wakes, Grief and Loss."
                ></i>
              </h2>
              <SpacerLine color="var(--crisis-color)" />
              <QuestionWrapper className="crisis">
                <h4>
                  {isNaN(timeShare.crisis)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.crisis)}
                </h4>
                <CustomSlider
                  value={isNaN(timeShare.crisis) ? 0 : timeShare.crisis}
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "crisis");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--crisis-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <h2 style={{ color: "var(--relationships-color)" }}>
                Relationships{" "}
                <Tooltip target=".relationshipTooltip" position="bottom" />
                <i
                  className="relationshipTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week dealing with matters such as relationships, marriage, divorce, and family dynamics."
                ></i>
              </h2>
              <SpacerLine color="var(--relationships-color)" />
              <QuestionWrapper className="relationships">
                <h4>
                  {isNaN(timeShare.relationships)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.relationships)}
                </h4>
                <CustomSlider
                  value={
                    isNaN(timeShare.relationships) ? 0 : timeShare.relationships
                  }
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "relationships");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--relationships-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>

            <SectionWrapper>
              <h2 style={{ color: "var(--lifeSkills-color)" }}>
                Life Skills{" "}
                <Tooltip target=".lifeSkillsTooltip" position="bottom" />
                <i
                  className="lifeSkillsTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week dealing with matters such as work, spiritual guidance, finances, behaviours, and connection to Church comunity."
                ></i>
              </h2>
              <SpacerLine color="var(--lifeSkills-color)" />
              <QuestionWrapper className="lifeSkills">
                <h4>
                  {isNaN(timeShare.lifeSkills)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.lifeSkills)}
                </h4>
                <CustomSlider
                  value={isNaN(timeShare.lifeSkills) ? 0 : timeShare.lifeSkills}
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "lifeSkills");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--lifeSkills-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>

            <SectionWrapper>
              <h2 style={{ color: "var(--health-color)" }}>
                Health <Tooltip target=".healthTooltip" position="bottom" />
                <i
                  className="healthTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week dealing with matters such as health, surgery, elderliness, long-term illness, and family health."
                ></i>
              </h2>
              <SpacerLine color="var(--health-color)" />
              <QuestionWrapper className="health">
                <h4>
                  {isNaN(timeShare.health)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.health)}
                </h4>
                <CustomSlider
                  value={isNaN(timeShare.health) ? 0 : timeShare.health}
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "health");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--health-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <h2 style={{ color: "var(--additionalNeeds-color)" }}>
                Additional Needs{" "}
                <Tooltip target=".addNeedsTooltip" position="bottom" />
                <i
                  className="addNeedsTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week assisting families and individuals with additional needs, such as those related to disabilities."
                ></i>
              </h2>
              <SpacerLine color="var(--additionalNeeds-color)" />
              <QuestionWrapper className="additionalNeeds">
                <h4>
                  {isNaN(timeShare.additionalNeeds)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.additionalNeeds)}
                </h4>
                <CustomSlider
                  value={
                    isNaN(timeShare.additionalNeeds)
                      ? 0
                      : timeShare.additionalNeeds
                  }
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "additionalNeeds");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--additionalNeeds-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <h2 style={{ color: "var(--mentalHealth-color)" }}>
                Mental Health{" "}
                <Tooltip target=".mentalTooltip" position="bottom" />
                <i
                  className="mentalTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week addressing issues related to depression, eating disorders, addictions, and other mental illnesses."
                ></i>
              </h2>
              <SpacerLine color="var(--mentalHealth-color)" />
              <QuestionWrapper className="mentalHealth">
                <h4>
                  {isNaN(timeShare.mentalHealth)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.mentalHealth)}
                </h4>
                <CustomSlider
                  value={
                    isNaN(timeShare.mentalHealth) ? 0 : timeShare.mentalHealth
                  }
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "mentalHealth");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--mentalHealth-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <h2 style={{ color: "var(--trauma-color)" }}>
                Trauma <Tooltip target=".traumaTooltip" position="bottom" />
                <i
                  className="traumaTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week assisting victims of abuse, bullying, and family violence."
                ></i>
              </h2>
              <SpacerLine color="var(--trauma-color)" />
              <QuestionWrapper className="trauma">
                <h4>
                  {isNaN(timeShare.trauma)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.trauma)}
                </h4>
                <CustomSlider
                  value={isNaN(timeShare.trauma) ? 0 : timeShare.trauma}
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "trauma");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--trauma-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <h2>
                Approximate Percentage of All Hours Spent In Person/Calls{" "}
                <Tooltip target=".inPersonTooltip" position="bottom" />
                <i
                  className="inPersonTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated proportion of all hours noted above spent in face-to-face interactions or phone calls, as opposed to time spent on emails and texts."
                ></i>
              </h2>
              <SpacerLine />
              <QuestionWrapper>
                <PercentSlider
                  inPersonHours={isNaN(timeInPerson) ? 0 : timeInPerson}
                  onChange={(newValue) => {
                    setTimeInPerson(newValue as number);
                  }}
                  totalHours={
                    timeShare.crisis +
                    timeShare.relationships +
                    timeShare.lifeSkills +
                    timeShare.health +
                    timeShare.additionalNeeds +
                    timeShare.mentalHealth +
                    timeShare.trauma
                  }
                  color="var(--accent-color-dark)"
                />
              </QuestionWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <h2 style={{ color: "var(--admin-color)" }}>
                Admin & Meetings{" "}
                <Tooltip target=".adminTooltip" position="bottom" />
                <i
                  className="adminTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent in meetings(excluding staff meetings) and administrative tasks. This will allow people leader to see how much time is spent on admin and meetings but they won't be able to see your individual number of hours."
                ></i>
              </h2>
              <SpacerLine color="var(--admin-color)" />
              <QuestionWrapper className="admin">
                <h4>
                  {isNaN(timeShare.admin)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.admin)}
                </h4>
                <CustomSlider
                  value={isNaN(timeShare.admin) ? 0 : timeShare.admin}
                  onChange={(newValue) => {
                    handleSliderChange(newValue as number, "admin");
                  }}
                  max={8}
                  step={0.5}
                  color="var(--admin-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <h2 style={{ color: "var(--personalDevelopment-color)" }}>
                Personal Development{" "}
                <Tooltip target=".persDevTooltip" position="bottom" />
                <i
                  className="persDevTooltip pi pi-info-circle"
                  data-pr-tooltip="An estimated number of hours spent this week reading materials, undergoing training, or participating in other activities for personal improvement."
                ></i>
              </h2>
              <SpacerLine color="var(--personalDevelopment-color)" />
              <QuestionWrapper className="personalDevelopment">
                <h4>
                  {isNaN(timeShare.personalDevelopment)
                    ? "0h"
                    : hoursToHoursMinutes(timeShare.personalDevelopment)}
                </h4>
                <CustomSlider
                  value={
                    isNaN(timeShare.personalDevelopment)
                      ? 0
                      : timeShare.personalDevelopment
                  }
                  onChange={(newValue) => {
                    handleSliderChange(
                      newValue as number,
                      "personalDevelopment"
                    );
                  }}
                  max={8}
                  step={0.5}
                  color="var(--personalDevelopment-color)"
                />
              </QuestionWrapper>
            </SectionWrapper>

            <HotTopicsMenu hotTopics={hotTopics} setHotTopics={setHotTopics} />

            <SectionWrapper>
              <h2>Your Report</h2>
              <SpacerLine />
              <QuestionWrapper>
                <h3>
                  <b>Hospital Visits</b>
                </h3>
                <NumberInput
                  value={teamLeaderReport.hospitalVisits}
                  onValueChange={(e) =>
                    handleNumberPickerChange(e, "hospitalVisits")
                  }
                />
              </QuestionWrapper>
            </SectionWrapper>
          </>
        )}

      {user &&
        user.PCRoles.some(
          (roleObj) =>
            roleObj.role === "oversight" && roleObj.teamId === selectedTeam
        ) && (
          <SectionWrapper>
            <h2>Your Team Report</h2>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Prepare and Enrich Appointments</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.prepareAndEnrich}
                onValueChange={(e) =>
                  handleNumberPickerChange(e, "prepareAndEnrich")
                }
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Weddings conducted</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.weddings}
                onValueChange={(e) => handleNumberPickerChange(e, "weddings")}
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>DFV Situations</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.dfv}
                onValueChange={(e) => handleNumberPickerChange(e, "dfv")}
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Funerals Conducted</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.funerals}
                onValueChange={(e) => handleNumberPickerChange(e, "funerals")}
              />
            </QuestionWrapper>
            <SpacerLine />

            <QuestionWrapper>
              <h3>
                <b>Death By Sucide</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.sucideDeath}
                onValueChange={(e) =>
                  handleNumberPickerChange(e, "sucideDeath")
                }
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Death Of A Child Under 10</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.childDeath}
                onValueChange={(e) => handleNumberPickerChange(e, "childDeath")}
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Death Of A Still Born</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.stillBorn}
                onValueChange={(e) => handleNumberPickerChange(e, "stillBorn")}
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Flower/Gifts Hampers</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.flowers}
                onValueChange={(e) => handleNumberPickerChange(e, "flowers")}
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Meals</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.meals}
                onValueChange={(e) => handleNumberPickerChange(e, "meals")}
              />
            </QuestionWrapper>
            <SpacerLine />
            <QuestionWrapper>
              <h3>
                <b>Kilo Of Kindness/Citycare Bags</b>
              </h3>
              <NumberInput
                value={teamLeaderReport.kiloOfKindness}
                onValueChange={(e) =>
                  handleNumberPickerChange(e, "kiloOfKindness")
                }
              />
            </QuestionWrapper>
            <SpacerLine />
          </SectionWrapper>
        )}

      <Button
        className="save-button"
        label="Save"
        severity="success"
        onClick={handleSave}
      />
      {!showIntroText && (
        <p className="intro hidden">
          Thank you for completing a pastoral care report. Please note that this
          report isn't intended to capture every hour of your work but only
          hours spent in key areas. <br />
          Your oversights will only be able to see a general overview of their
          team to identify trends and provide information for our annual report. 
          <br />
          No one except you can have access to your individual data.
        </p>
      )}
      <Lottie
        className="confetti-lottie"
        animationData={confettiLottie}
        play={confetti}
        loop={true}
      />
    </ReportPageWrapper>
  );
}
